import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { DataTable } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { ContactDialog } from "@/components/crm/ContactDialog";
import { Button } from "@/components/ui/button";
import { Users, Plus, Pencil, Trash2 } from "lucide-react";
import { formatPhone } from "@/lib/phone";
import { toast } from "sonner";

export default function People() {
  const { has } = useAtlas();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [dialog, setDialog] = useState(null); // null | {} (new) | contact (edit)
  const canManage = has("people.manage");

  const load = () => api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (has("organizations.view")) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orgName = (oid) => orgs.find((o) => o.id === oid)?.name;
  const remove = async (p) => {
    if (!window.confirm(`Delete contact "${p.first_name} ${p.last_name}"? This cannot be undone.`)) return;
    try { await api.delete(`/people/${p.id}`); toast.success("Contact deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "name", header: "Name", render: (r) => <span className="font-medium text-slate-900">{r.first_name} {r.last_name}</span> },
    { key: "title", header: "Title", render: (r) => r.title || <span className="text-slate-300">—</span> },
    { key: "org", header: "Organization", render: (r) => orgName(r.organization_id) || <span className="text-slate-300">—</span> },
    { key: "email", header: "Email", render: (r) => r.email || <span className="text-slate-300">—</span> },
    { key: "phone", header: "Phone", render: (r) => (r.phone && formatPhone(r.phone)) || <span className="text-slate-300">—</span> },
    ...(canManage ? [{ key: "actions", header: "", render: (r) => (
      <div className="flex items-center gap-1 justify-end">
        <button onClick={(e) => { e.stopPropagation(); setDialog(r); }} className="p-1.5 text-slate-400 hover:text-slate-800 rounded hover:bg-slate-100" data-testid={`person-edit-${r.id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
        <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50" data-testid={`person-delete-${r.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    ) }] : []),
  ];

  return (
    <div>
      <PageHeader title="People" subtitle="Shared Atlas Core records, one identity reused everywhere">
        {canManage && <Button onClick={() => setDialog({})} data-testid="person-create-button"><Plus className="w-4 h-4 mr-1" /> New Person</Button>}
      </PageHeader>

      {people.length === 0 ? (
        <EmptyState icon={Users} title="No people yet" description="Add people known to your business. The same Person is reused across all applications." actionLabel={canManage ? "New Person" : undefined} onAction={() => setDialog({})} />
      ) : (
        <DataTable testid="crm-people-table" rowTestId="person-row" columns={columns} rows={people} onRowClick={(r) => navigate(`/crm/people/${r.id}`)} />
      )}

      <ContactDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} contact={dialog?.id ? dialog : null} orgs={orgs} onSaved={load} />
    </div>
  );
}
